import { Link } from "react-router-dom";
import { ArrowLeft, Package, CheckCircle2, Factory, Wrench, ShieldCheck, Truck, Globe, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import logo from "@/assets/arabian-alwaha-logo.jpg";
import heroImg from "@/assets/hero-oil-gas.jpg";

const Trading = () => {
  const { language } = useLanguage();
  const isArabic = language === "ar";

  const t = {
    back: isArabic ? "العودة إلى الموقع" : "Back to website",
    eyebrow: isArabic ? "خدماتنا · التجارة" : "Our services · Trading",
    title: isArabic ? "تجارة المواد والإمدادات الصناعية" : "Materials & Industrial Trading",
    lede: isArabic
      ? "نوفر مواد البناء والإمدادات الصناعية ذات الجودة العالية من شركاء عالميين موثوقين، بأسعار تنافسية ومواعيد تسليم دقيقة."
      : "Premium construction materials and industrial supplies sourced from trusted global partners — competitively priced, precisely scheduled.",
    overviewTitle: isArabic ? "نبذة عن خدمة التجارة" : "About our trading practice",
    overviewBody: isArabic
      ? "منذ عام ٢٠٠٨، تعمل العربية الواحة كحلقة وصل موثوقة بين المصنّعين العالميين والمشاريع الكبرى في المملكة العربية السعودية ودول الخليج. نوفر سلسلة توريد قصيرة، مع التزام صارم بالمواصفات الفنية وشهادات الجودة المعتمدة."
      : "Since 2008, Arabian Alwaha has acted as a dependable bridge between global manufacturers and major projects across Saudi Arabia and the GCC. We deliver a short supply chain, strict adherence to technical specifications, and certified quality at every step.",
    capabilitiesTitle: isArabic ? "إمكاناتنا الرئيسية" : "Key capabilities",
    capabilities: [
      { icon: Layers, title: isArabic ? "مواد البناء" : "Construction materials", body: isArabic ? "حديد، أسمنت، خرسانة، ومواد عزل لأكبر المشاريع." : "Steel, cement, concrete, insulation and finishing materials at project scale." },
      { icon: Wrench, title: isArabic ? "الإمدادات الصناعية" : "Industrial supplies", body: isArabic ? "أدوات، صمامات، مضخات، وتجهيزات لخطوط الإنتاج والصيانة." : "Tools, valves, pumps and fittings for production lines and maintenance." },
      { icon: ShieldCheck, title: isArabic ? "السلامة والمعدات الواقية" : "Safety & PPE", body: isArabic ? "معدات حماية شخصية معتمدة دولياً مع توثيق كامل." : "Internationally certified personal protective equipment with full documentation." },
      { icon: Truck, title: isArabic ? "اللوجستيات والتسليم" : "Logistics & delivery", body: isArabic ? "إدارة شحن متعددة الوسائط مع تتبع دقيق إلى موقع المشروع." : "Multi-modal freight management with precise tracking to project site." },
      { icon: Globe, title: isArabic ? "التوريد الدولي" : "International sourcing", body: isArabic ? "شبكة مصنّعين معتمدين في أوروبا وآسيا والشرق الأوسط." : "Vetted manufacturer network across Europe, Asia and the Middle East." },
      { icon: CheckCircle2, title: isArabic ? "ضبط الجودة" : "Quality assurance", body: isArabic ? "فحص ما قبل الشحن وشهادات مطابقة لكل دفعة." : "Pre-shipment inspection and certificates of conformity for every batch." },
    ],
    industriesTitle: isArabic ? "الصناعات التي نخدمها" : "Industries we serve",
    industriesLede: isArabic
      ? "نخدم القطاعات الأكثر تطلباً في المملكة، حيث الجودة والسلامة والالتزام الزمني ليست خيارات بل ضرورة."
      : "We serve the Kingdom's most demanding sectors — where quality, safety and on-time delivery are non-negotiable.",
    industries: [
      { name: isArabic ? "النفط والغاز" : "Oil & Gas", desc: isArabic ? "توريد للمصافي ومحطات المعالجة وحقول الإنتاج." : "Supply to refineries, processing plants and production fields." },
      { name: isArabic ? "البتروكيماويات" : "Petrochemicals", desc: isArabic ? "مواد متخصصة لمشاريع البتروكيماويات الكبرى." : "Specialized materials for major petrochemical projects." },
      { name: isArabic ? "التعدين والمعادن" : "Mining & Minerals", desc: isArabic ? "إمدادات صناعية لمنشآت التعدين ومعالجة الفوسفات." : "Industrial supplies for mining sites and phosphate processing." },
      { name: isArabic ? "الموانئ والبنية التحتية" : "Ports & Infrastructure", desc: isArabic ? "مواد البناء الثقيلة لمشاريع الموانئ والطرق." : "Heavy-duty construction materials for ports and roads." },
      { name: isArabic ? "الطاقة والمرافق" : "Power & Utilities", desc: isArabic ? "تجهيزات كهربائية وميكانيكية لمحطات الطاقة." : "Electrical and mechanical equipment for power facilities." },
      { name: isArabic ? "البناء التجاري" : "Commercial Construction", desc: isArabic ? "حلول التشطيب والمواد للمشاريع التجارية الكبرى." : "Finishing solutions and materials for large commercial projects." },
    ],
    ctaTitle: isArabic ? "هل تحتاج إلى عرض سعر للمواد؟" : "Need a materials quote?",
    ctaBody: isArabic ? "أرسل لنا قائمة احتياجاتك وسنرد خلال ساعات." : "Send us your requirements and we'll respond within hours.",
    ctaPrimary: isArabic ? "اطلب عرض سعر" : "Request a quote",
    ctaSecondary: isArabic ? "تصفح كل الخدمات" : "Browse all services",
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isArabic ? "rtl" : "ltr"}>
      {/* Sticky chrome */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/40">
        <div className="container mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, hsl(160 65% 12% / 0.96) 0%, hsl(160 55% 22% / 0.92) 55%, hsl(45 80% 30% / 0.85) 100%)" }} aria-hidden="true" />
        <div className="relative container mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-white/95 p-1.5 shadow-elegant">
                <img src={logo} alt="Arabian Alwaha" className="w-full h-full object-contain" />
              </div>
              <p className="text-secondary text-xs uppercase tracking-[0.3em] font-bold">{t.eyebrow}</p>
            </div>
            <h1 className="text-white font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">{t.title}</h1>
            <p className="text-white/85 text-lg lg:text-xl max-w-2xl leading-relaxed">{t.lede}</p>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 lg:py-24">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-4">{isArabic ? "نبذة" : "Overview"}</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-accent mb-6">{t.overviewTitle}</h2>
          <p className="text-lg text-muted-foreground leading-relaxed">{t.overviewBody}</p>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-20 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-4">{isArabic ? "الإمكانات" : "Capabilities"}</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-accent">{t.capabilitiesTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.capabilities.map((c) => (
              <div key={c.title} className="bg-card rounded-xl p-6 shadow-soft hover:shadow-elegant transition-elegant border border-border/40">
                <div className="w-12 h-12 rounded-lg gradient-primary flex items-center justify-center mb-4">
                  <c.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-accent mb-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 lg:py-24">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-4">{isArabic ? "القطاعات" : "Industries"}</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-accent mb-4">{t.industriesTitle}</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{t.industriesLede}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.industries.map((i) => (
              <div key={i.name} className="border border-border/60 rounded-xl p-6 hover:border-primary/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Factory className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold text-accent">{i.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{i.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-primary to-primary/80">
        <div className="container mx-auto px-6 lg:px-10 text-center">
          <Package className="w-12 h-12 text-secondary mx-auto mb-6" />
          <h2 className="text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">{t.ctaTitle}</h2>
          <p className="text-primary-foreground/85 text-lg mb-8 max-w-xl mx-auto">{t.ctaBody}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-secondary text-accent hover:bg-secondary/90"><Link to="/#contact">{t.ctaPrimary}</Link></Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 text-white border-white/40 hover:bg-white/20"><Link to="/#services">{t.ctaSecondary}</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Trading;